import { Navigate, Route, Router } from '@solidjs/router'
import { createQuery, useQueryClient } from '@tanstack/solid-query'
import { RESOURCES } from '@veem/crm-schema'
import { createEffect, createSignal, onCleanup, Show, untrack } from 'solid-js'
import { SignIn } from './components/sign-in.tsx'
import { api } from './lib/api.ts'
import { INVALIDATION_MAP, queryKeys } from './lib/query-keys.ts'
import { readSession, session, setSession } from './lib/session.ts'
import { copySnapshot, withSnapshotHydration } from './lib/snapshot-value.ts'
import { hydrateData } from './prototype/data/hydration.ts'
import { PEOPLE } from './prototype/data/people.ts'
import { prototypeRoutes } from './prototype/index.tsx'
import { type ProtoState, proto } from './prototype/state/store.ts'

export interface Snapshot {
  data: Record<string, unknown>
  state: Partial<ProtoState>
}

const Loading = () => (
  <main class="grid min-h-dvh place-items-center bg-app text-body text-text-muted" role="status">
    Завантажуємо робочий простір…
  </main>
)

export const App = () => {
  const client = useQueryClient()
  const currentSession = createQuery(() => ({
    queryKey: queryKeys.session,
    queryFn: readSession,
    retry: false,
    staleTime: 60_000,
  }))
  const [hydratedFor, setHydratedFor] = createSignal<string | null>(null)
  const [refreshFailed, setRefreshFailed] = createSignal(false)
  const refreshConflict = () => {
    void client.invalidateQueries({
      predicate: (query) => query.queryKey[0] === 'crm' && query.queryKey[1] !== 'session',
    })
  }
  window.addEventListener('crm:conflict', refreshConflict)
  onCleanup(() => window.removeEventListener('crm:conflict', refreshConflict))
  const failedRefresh = () => setRefreshFailed(true)
  window.addEventListener('crm:refresh-failed', failedRefresh)
  onCleanup(() => window.removeEventListener('crm:refresh-failed', failedRefresh))
  createEffect(() => {
    setSession(currentSession.data ?? null)
  })
  const snapshot = createQuery(() => ({
    queryKey: [...queryKeys.snapshot(session()?.actor.id ?? ''), session()?.actor.role],
    queryFn: () => api<Snapshot>('/snapshot'),
    enabled: !!session(),
    staleTime: 30_000,
  }))
  const retrySnapshot = async () => {
    const result = await snapshot.refetch()
    if (!result.isError) setRefreshFailed(false)
  }

  let appliedSnapshot: Snapshot | undefined
  let appliedIdentity = ''
  createEffect(() => {
    const value = snapshot.data
    const current = session()
    if (!value || !current) return
    const identity = `${current.actor.id}:${current.actor.role}`
    if (value === appliedSnapshot && identity === appliedIdentity) return
    untrack(() => {
      const incoming = copySnapshot(value)
      hydrateData(incoming.data, { replaceStaff: identity !== appliedIdentity })
      proto.hydrate(incoming.state)
      withSnapshotHydration(() => {
        const person = PEOPLE.find((candidate) => candidate.id === current.actor.id)
        if (!person) PEOPLE.push(copySnapshot(current.person))
      })
      appliedSnapshot = value
      appliedIdentity = identity
      setHydratedFor(identity)
    })
  })

  createEffect(() => {
    const current = session()
    if (!current) return
    const stream = new EventSource('/api/events', { withCredentials: true })
    const refresh = (event: MessageEvent) => {
      let topic = event.type
      try {
        const payload = JSON.parse(event.data)
        topic = payload.topic ?? payload.type ?? topic
      } catch {}
      void client.invalidateQueries({ queryKey: queryKeys.snapshot(current.actor.id) })
      for (const module of INVALIDATION_MAP[topic] ?? []) {
        void client.invalidateQueries({ queryKey: queryKeys.module(module) })
      }
      if (topic === 'staff.changed' || topic === 'directory.changed') {
        void client.invalidateQueries({ queryKey: queryKeys.staff })
        void client.invalidateQueries({ queryKey: queryKeys.session })
      }
    }
    stream.onerror = () => {
      void client.invalidateQueries({ queryKey: queryKeys.session })
    }
    stream.onmessage = refresh
    // Hints sent while the stream was down are lost, so a reconnect must refetch everything.
    stream.onopen = () => {
      void client.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'crm' && query.queryKey[1] !== 'session',
      })
    }
    for (const topic of [
      ...new Set([
        ...RESOURCES.map((resource) => `${resource}.changed`),
        ...Object.keys(INVALIDATION_MAP),
        'changed',
        'invalidate',
      ]),
    ])
      stream.addEventListener(topic, refresh)
    onCleanup(() => stream.close())
  })

  return (
    <Show when={!currentSession.isPending} fallback={<Loading />}>
      <Show
        when={!currentSession.isError || !!currentSession.data}
        fallback={
          <main class="grid min-h-dvh place-content-center gap-3 p-6">
            <p role="alert" class="text-body">
              Не вдалося підключитися до сервера.
            </p>
            <button
              type="button"
              class="text-body underline"
              onClick={() => void currentSession.refetch()}
            >
              Спробувати знову
            </button>
          </main>
        }
      >
        <Show
          when={session()}
          fallback={
            <SignIn
              onSignedIn={() => {
                window.history.replaceState(null, '', '/prototype/home')
                return currentSession.refetch()
              }}
            />
          }
        >
          <Show
            when={!snapshot.isError || !!snapshot.data}
            fallback={
              <main class="grid min-h-dvh place-content-center gap-3 p-6">
                <p role="alert" class="text-body">
                  Не вдалося завантажити дані.
                </p>
                <button
                  type="button"
                  class="text-body underline"
                  onClick={() => void snapshot.refetch()}
                >
                  Спробувати знову
                </button>
              </main>
            }
          >
            <Show
              when={hydratedFor() === `${session()?.actor.id}:${session()?.actor.role}`}
              fallback={<Loading />}
            >
              <Router>
                {prototypeRoutes()}
                <Route path="*" component={() => <Navigate href="/prototype/home" />} />
              </Router>
              <Show when={refreshFailed()}>
                <div
                  role="alert"
                  class="fixed inset-x-4 top-3 z-[100] mx-auto flex max-w-xl items-center gap-3 rounded-control border border-status-warning bg-surface p-3 text-body text-text shadow-lg"
                >
                  <span>Зміни збережено, але не вдалося оновити дані.</span>
                  <button
                    type="button"
                    class="ml-auto shrink-0 underline"
                    disabled={snapshot.isFetching}
                    onClick={retrySnapshot}
                  >
                    Оновити дані
                  </button>
                </div>
              </Show>
            </Show>
          </Show>
        </Show>
      </Show>
    </Show>
  )
}
